import { Link } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import { Logo } from '@/shared/components/ui/Logo'
import { appConfig, appVersion } from '@/lib/appConfig'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useAuth } from '@/shared/contexts/AuthContext'
import { usePwaInstall } from '@/shared/hooks/usePwaInstall'
import { useContextKey, useUnit } from '@/modules/units/contexts/UnitContext'
import { usePermissions } from '@/modules/units/usePermissions'
import { ContextIcon } from '@/shared/components/ui/ContextIcon'
// import { CategoryDot } from '@/components/ui'
import { TeamModal } from '@/modules/units'
import { listGroupsByContext, getEnabledGroups } from '@/modules/units/context-settings'
import type { UnitContext } from '@/modules/units/contexts/UnitContextTypes'

interface MenuItem {
  name: string
  href: string
  icon: React.ReactNode
  key?: string  // Unique key for tracking expansion state
  onClick?: () => void
  discreet?: boolean
  subItems?: MenuItem[]  // Support for sub-items
  isCollapsible?: boolean  // Item can be expanded/collapsed
}

interface MenuSection {
  title?: string
  items: MenuItem[]
}

export function Sidebar({ isMobileOpen, onClose }: { isMobileOpen?: boolean; onClose?: () => void }) {
  const location = useLocation(); const pathname = location.pathname
  const { t } = useTranslation()
  const { user: authUser } = useAuth()
  const { available: pwaAvailable, isStandalone } = usePwaInstall()
  const appContext = useContextKey() as UnitContext
  const { currentUnit, userRole } = useUnit()
  const permissions = usePermissions(userRole)
  const isMember = userRole === 'viewer'

  const [menuSections, setMenuSections] = useState<MenuSection[]>([])
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['assets', 'settings', 'assetsettings'])) // Start with assets and settings expanded
  const [showTeamModal, setShowTeamModal] = useState(false)

  // Auto-expand items based on current route
  useEffect(() => {
    if (pathname.startsWith('/assets')) {
      setExpandedItems(prev => new Set(prev).add('assets'))
    }
    if (pathname.startsWith('/settings')) {
      setExpandedItems(prev => {
        const newSet = new Set(prev)
        newSet.add('settings')
        if (pathname.startsWith('/settings/assets')) {
          newSet.add('assetsettings')
        }
        return newSet
      })
    }    if (pathname.startsWith('/poultry/feed')) {
      setExpandedItems(prev => new Set(prev).add('poultryFeed'))
    }
    if (pathname.startsWith('/poultry/registrations')) {
      setExpandedItems(prev => new Set(prev).add('poultryRegistrations'))
    }  }, [pathname])

  const toggleItem = (itemName: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemName)) {
        newSet.delete(itemName)
      } else {
        newSet.add(itemName)
      }
      return newSet
    })
  }

  useEffect(() => {
    let mounted = true

    const loadMenu = async () => {
      if (!appContext) return
      try {
        // Accounting context: office dashboard and team access.
        if (appContext === 'accounting') {
          const isAccountingClient = currentUnit?.attrs?.accounting_unit_type === 'client'
          const accountingBase: MenuSection = {
            items: [
              {
                name: isAccountingClient
                  ? t('accounting.clientPortal.navigation.documents')
                  : t('navigation.homePage'),
                href: '/accounting',
                icon: <ContextIcon context="accounting" className="h-5 w-5" />,
              },
              ...(!isAccountingClient ? [{
                name: t('accounting.layouts.navigation'),
                href: '/accounting/layouts',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h10M8 4v4m6 2v4m4 2v4" />
                  </svg>
                ),
              }] : []),
            ],
          }
          const accountingFooter: MenuSection = {
            items: currentUnit?.id && permissions.canManageTeam ? [
              {
                name: t('navigation.manageTeam'),
                href: '#',
                onClick: () => setShowTeamModal(true),
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                ),
              },
            ] : [],
          }
          if (mounted) setMenuSections([accountingBase, accountingFooter])
          return
        }

        // ── Billing context: fixed menu, no asset group loading ─────────────
        if (appContext === 'billing') {
          const billingBase: MenuSection = {
            items: [
              {
                name: t('navigation.homePage'),
                href: '/billing',
                icon: <ContextIcon context="billing" className="h-5 w-5" />,
              },
            ],
          }
          const billingMain: MenuSection = {
            items: [
              {
                name: t('navigation.receivable'),
                href: '/billing/receivable',
                icon: (
                  // Trending arrow up + currency circle  →  "dinheiro entrando"
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
              },
              {
                name: t('navigation.payable'),
                href: '/billing/payable',
                icon: (
                  // Wallet / banknote  →  "dinheiro saindo"
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
              },
              {
                name: t('registrations.contacts.list.title'),
                href: '/registrations/billing/contacts',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
              },
            ],
          }
          const billingFooter: MenuSection = {
            items: currentUnit?.id && permissions.canManageTeam ? [
              {
                name: t('navigation.manageTeam'),
                href: '#',
                onClick: () => setShowTeamModal(true),
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                ),
              },
            ] : [],
          }
          if (mounted) setMenuSections([billingBase, billingMain, billingFooter])
          return
        }

        // ── Pricing context: fixed menu for pricing calculator ───────────────
        if (appContext === 'pricing') {          const pricingBase: MenuSection = {
            items: [
              {
                name: t('navigation.homePage'),
                href: '/pricing',
                icon: <ContextIcon context="pricing" className="h-5 w-5" />,
              },
            ],
          }
          const pricingMain: MenuSection = {
            items: [
              {
                name: t('pricing.products'),
                href: '/pricing/produtos',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m0 0L4 7m8 4v10m8-5l-8 4-8-4" />
                  </svg>
                ),
              },
              {
                name: t('pricing.materials'),
                href: '/pricing/insumos',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                ),
              },
              {
                name: t('pricing.simulator'),
                href: '/pricing/simulador',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
              },
              {
                name: t('pricing.fixedCosts'),
                href: '/pricing/custos-fixos',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
              },
            ],
          }
          const pricingFooter: MenuSection = {
            items: currentUnit?.id && permissions.canManageTeam ? [
              {
                name: t('navigation.manageTeam'),
                href: '#',
                onClick: () => setShowTeamModal(true),
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                ),
              },
            ] : [],
          }
          if (mounted) setMenuSections([pricingBase, pricingMain, pricingFooter])
          return
        }

        // ── poultry context: fixed menu for poultry farm management ───────────
        if (appContext === 'poultry') {
          const poultryBase: MenuSection = {
            items: [
              {
                name: t('navigation.homePage'),
                href: '/poultry',
                icon: <ContextIcon context="poultry" className="h-5 w-5" />,
              },
            ],
          }
          const poultryMain: MenuSection = {
            items: [
              {
                name: t('navigation.poultry.birds'),
                href: '/poultry/batches',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                ),
              },
              {
                name: t('navigation.poultry.eggProduction'),
                href: '/poultry/egg-production',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2C8.686 2 6 6.477 6 10c0 4.418 2.686 8 6 8s6-3.582 6-8c0-3.523-2.686-8-6-8z" />
                  </svg>
                ),
              },
              {
                name: t('navigation.poultry.mortalities'),
                href: '/poultry/mortalities',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
              },
              {
                name: t('navigation.poultry.feed'),
                href: '/poultry/feed',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                ),
              },
              {
                name: t('navigation.poultry.medications'),
                href: '/poultry/medications',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                ),
              },
              {
                name: t('navigation.poultry.slaughters'),
                href: '/poultry/slaughters',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
              },
              {
                name: t('navigation.poultry.veterinary'),
                href: '/poultry/veterinary-visits',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                ),
              },
              {
                name: t('navigation.poultry.registrations'),
                href: '/poultry/registrations',
                key: 'poultryRegistrations',
                icon: (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                ),
                isCollapsible: true,
                subItems: [
                  {
                    name: t('navigation.poultry.breeds'),
                    href: '/poultry/registrations/breeds',
                    icon: (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    ),
                  },
                  {
                    name: t('navigation.poultry.contacts'),
                    href: '/registrations/poultry/contacts',
                    icon: (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    ),
                  },
                  {
                    name: t('navigation.poultry.packageSizes'),
                    href: '/poultry/registrations/package-sizes',
                    icon: (
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                    ),
                  },
                ],
              },
            ],
          }
          const poultryFooter: MenuSection = {
            items: [
              ...(currentUnit?.id && permissions.canManageTeam ? [
                {
                  name: t('navigation.manageTeam'),
                  href: '#',
                  onClick: () => setShowTeamModal(true),
                  icon: (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  ),
                },
              ] : []),
            ],
          }
          if (mounted) setMenuSections([poultryBase, poultryMain, poultryFooter])
          return
        }

        // ── Other contexts: load dynamic asset groups ────────────────────────
        const groups = await listGroupsByContext(appContext)

        // Determine enabled ids: prefer currentUnit.enabled_group_ids if available
        let enabledIds: string[] | null = null
        if (currentUnit?.enabled_group_ids && currentUnit.enabled_group_ids.length > 0) {
          enabledIds = currentUnit.enabled_group_ids
        } else if (currentUnit?.id) {
          const enabled = await getEnabledGroups(currentUnit.id)
          enabledIds = enabled.map(e => e.group_id)
        }

        // Filter groups: show only those that are enabled (is_active=true) for the current unit
        const visibleGroups = groups.filter((g: any) => {
          if (enabledIds) return enabledIds.includes(g.id)
          return g.is_active !== false // Show if is_active is true or undefined (for backward compatibility)
        })

        // // Build asset subitems from groups
        // const assetSubItems: MenuItem[] = visibleGroups.map((g: any) => {
        //   // Try to get translation from navigation.{groupKey}, fallback to key itself
        //   const translationKey = `navigation.${g.key}`
        //   const label = t(translationKey) !== translationKey ? t(translationKey) : g.key
        //   console.log(`[Sidebar] Group ${g.key}: translationKey=${translationKey}, label=${label}`)
          
        //   return {
        //     name: label,
        //     href: `/assets/${g.key}`,
        //     icon: <CategoryDot active={pathname === `/assets/${g.key}`} className="h-5 w-5" />,
        //   }
        // })

        // Build base dashboard/home section with current context so icon updates
        const baseSection: MenuSection = {
          items: [
            {
              name: t('navigation.homePage'),
              href: '/dashboard',
              icon: <ContextIcon context={appContext} className="h-5 w-5" />,
            },
          ],
        }

        // Build movements section
        const movementsSection: MenuSection = {
          items: [
            {
              name: t('navigation.movements'),
              href: '/movements',
              icon: (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              ),
            },
          ],
        }

        // Build assets section with collapsible subitems
        // const assetsSection: MenuSection = {
        //   items: [
        //     {
        //       name: t('navigation.assets'),
        //       href: '/assets',
        //       key: 'assets',
        //       icon: (
        //         <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        //           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        //         </svg>
        //       ),
        //       isCollapsible: true,
        //       subItems: assetSubItems,
        //     },
        //   ],
        // }

        // Build footer section with settings
        // const footerSection: MenuSection = {
        //   items: [
        //     {
        //       name: t('navigation.settings'),
        //       href: '/settings',
        //       key: 'settings',
        //       icon: (
        //         <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        //           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        //           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        //         </svg>
        //       ),
        //       isCollapsible: true,
        //       subItems: [
        //         {
        //           name: t('navigation.assetSettings'),
        //           href: '/settings/assets',
        //           key: 'assetsettings',
        //           icon: (
        //             <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        //               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        //             </svg>
        //           ),
        //           isCollapsible: true,
        //           subItems: [
        //             {
        //               name: t('navigation.categories'),
        //               href: '/settings/assets/categories',
        //               icon: (
        //                 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        //                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        //                 </svg>
        //               ),
        //             },
        //             {
        //               name: t('navigation.groups'),
        //               href: '/settings/assets/groups',
        //               icon: (
        //                 <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        //                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        //                 </svg>
        //               ),
        //             },
        //           ],
        //         },
        //       ],
        //     },
        //   ],
        // }

        // Merge sections: base, assets (with subitems), movements, footer
      //   if (mounted) setMenuSections([baseSection, assetsSection, movementsSection, footerSection])
      } catch (error) {
        console.error('[Sidebar] Error loading dynamic menu:', error)
      }
    }

    loadMenu()
    return () => { mounted = false }
  }, [appContext, currentUnit, t])

  const isProd = import.meta.env.MODE === 'production' || import.meta.env.PROD

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/80 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 transform bg-white dark:bg-gray-900 shadow-xl transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-0 flex flex-col
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="flex h-16 items-center gap-3 border-b border-gray-200 dark:border-gray-800 px-4">
          <Logo size="sm" />
          <span className="text-lg font-bold text-gray-900 dark:text-white truncate">
            {appConfig.name}
          </span>
        </div>

        {/* Menu */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {menuSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className={sectionIndex > 0 ? 'mt-6' : ''}>
              {section.title && (
                <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {section.title}
                </h3>
              )}
              <ul className="space-y-1">
                {section.items.map((item) => {
                  const isActive = pathname === item.href
                  const itemKey = item.key || item.name.toLowerCase()
                  const isExpanded = expandedItems.has(itemKey)
                  const hasSubItems = item.subItems && item.subItems.length > 0
                  
                  // Hide install menu item only when already running as installed PWA (standalone mode)
                  if (item.key === 'install' && isStandalone) return null
                  
                  return (
                    <li key={item.href}>
                      {/* Main item */}
                      {item.isCollapsible && hasSubItems ? (
                        // Collapsible item with subitems
                        <div>
                          <button
                            onClick={() => toggleItem(itemKey)}
                            className={`
                              w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors
                              ${
                                isActive || 
                                (item.href === '/assets' && pathname.startsWith('/assets/')) ||
                                (item.href === '/settings' && pathname.startsWith('/settings'))
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                              }
                            `}
                          >
                            <span className={
                              isActive || 
                              (item.href === '/assets' && pathname.startsWith('/assets/')) ||
                              (item.href === '/settings' && pathname.startsWith('/settings'))
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-gray-400'
                            }>
                              {item.icon}
                            </span>
                            <span className="flex-1 text-left">{item.name}</span>
                            <svg 
                              className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {/* Subitems */}
                          {isExpanded && hasSubItems && (
                            <ul className="mt-1 ml-4 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                              {item.subItems!.map((subItem) => {
                                const isSubActive = pathname === subItem.href
                                const subItemKey = subItem.key || subItem.name.toLowerCase()
                                const isSubExpanded = expandedItems.has(subItemKey)
                                const hasSubSubItems = subItem.subItems && subItem.subItems.length > 0
                                
                                return (
                                  <li key={subItem.href}>
                                    {/* Sub-item with potential sub-subitems */}
                                    {subItem.isCollapsible && hasSubSubItems ? (
                                      <div>
                                        <button
                                          onClick={() => toggleItem(subItemKey)}
                                          className={`
                                            w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors
                                            ${
                                              isSubActive || pathname.startsWith(subItem.href + '/')
                                                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                                            }
                                          `}
                                        >
                                          <span className={
                                            isSubActive || pathname.startsWith(subItem.href + '/')
                                              ? 'text-green-600 dark:text-green-400' 
                                              : 'text-gray-400'
                                          }>
                                            {subItem.icon}
                                          </span>
                                          <span className="flex-1 text-left">{subItem.name}</span>
                                          <svg 
                                            className={`h-3 w-3 transition-transform ${isSubExpanded ? 'rotate-180' : ''}`}
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                          >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                          </svg>
                                        </button>
                                        
                                        {/* Sub-subitems (level 3) */}
                                        {isSubExpanded && hasSubSubItems && (
                                          <ul className="mt-1 ml-4 space-y-1 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                                            {subItem.subItems!.map((subSubItem) => {
                                              const isSubSubActive = pathname === subSubItem.href
                                              return (
                                                <li key={subSubItem.href}>
                                                  <Link to={subSubItem.href}
                                                    onClick={onClose}
                                                    className={`
                                                      flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors
                                                      ${
                                                        isSubSubActive
                                                          ? 'bg-green-50 dark:bg-green-900/10 text-green-700 dark:text-green-400'
                                                          : 'text-gray-500 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/30'
                                                      }
                                                    `}
                                                  >
                                                    <span className={isSubSubActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                                                      {subSubItem.icon}
                                                    </span>
                                                    {subSubItem.name}
                                                  </Link>
                                                </li>
                                              )
                                            })}
                                          </ul>
                                        )}
                                      </div>
                                    ) : (
                                      <Link to={subItem.href}
                                        onClick={onClose}
                                        className={`
                                          flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors
                                          ${
                                            isSubActive
                                              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                                          }
                                        `}
                                      >
                                        <span className={isSubActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                                          {subItem.icon}
                                        </span>
                                        {subItem.name}
                                      </Link>
                                    )}
                                  </li>
                                )
                              })}
                            </ul>
                          )}
                        </div>
                      ) : (
                        // Regular link item or action button
                        item.onClick ? (
                          item.key === 'install' ? (
                            // Install PWA — destaque visual especial
                            <button
                              onClick={() => {
                                onClose?.()
                                void item.onClick!()
                              }}
                              className="group w-full text-left flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 border border-blue-200/60 dark:border-blue-800/30 bg-blue-50/40 dark:bg-blue-900/10 text-blue-600 dark:text-blue-400 hover:bg-blue-50/70 dark:hover:bg-blue-900/20 hover:border-blue-300/70 dark:hover:border-blue-700/40"
                            >
                              <span className="text-blue-400 dark:text-blue-500 transition-transform duration-200 group-hover:-translate-y-0.5">
                                {item.icon}
                              </span>
                              <span className="flex-1">{item.name}</span>
                              {pwaAvailable && (
                                <span className="h-1.5 w-1.5 rounded-full bg-blue-400 dark:bg-blue-500" />
                              )}
                            </button>
                          ) : (
                          <button
                            onClick={() => {
                              onClose?.()
                              void item.onClick!()
                            }}
                            className={`
                              w-full text-left flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors
                              ${
                                isActive
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                              }
                            `}
                          >
                            <span className={isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                              {item.icon}
                            </span>
                            {item.name}
                          </button>
                          )
                        ) : (
                          <Link to={item.href}
                            onClick={onClose}
                            className={`
                              flex items-center gap-3 rounded-lg px-3 ${item.discreet ? 'py-1.5' : 'py-2.5'} text-sm font-medium transition-colors
                              ${
                                isActive
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                              }
                              ${item.discreet ? 'text-gray-500 dark:text-gray-400' : ''}
                            `}
                          >
                            <span className={item.discreet ? 'text-gray-400' : (isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400')}>
                              {item.icon}
                            </span>
                            {item.name}
                          </Link>
                        )
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer: build version */}
        <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
          <div className="truncate">{t('navigation.footer.version')}: v{appVersion}</div>
          {!isProd && (
            <div className="truncate">{t('navigation.footer.environment')}: {import.meta.env.MODE}</div>
          )}
        </div>
      </aside>

      {currentUnit?.id && (
        <TeamModal
          unitId={currentUnit.id}
          isOpen={showTeamModal}
          onClose={() => setShowTeamModal(false)}
        />
      )}
    </>
  )
}

